import { faSquare } from '@fortawesome/free-regular-svg-icons';
import {
  faCheck,
  faCheckSquare,
  faCog,
  faGear,
  faInfoCircle,
  faLink,
  faPrint,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { Button, Input, Label, UncontrolledTooltip } from 'reactstrap';
import Swal from 'sweetalert2';
import { clone } from '../../../../../Services/schedule';
import '../Schedule.style.css';
import style from './style';
import { DepartmentContext } from '../../../../../DepartmentWrapper';
import {
  formatMeetingUrlForDisplay,
  isRezrateEmbeddedMeetingUrl,
  REZRATE_EMBEDDED_MEETING_PLACEHOLDER,
  urlWithHttpProtocol,
} from 'Common.functions';

const CustomEventPopover = ({
  setShowAddCustomPopover,
  row,
  addRowCustomEvent,
  hideHeaderPopover,
  scheduleData,
  fireConfirmationForEditingPastSchedule,
  dateIsInThePast,
  willEditThePast,
  onChangeDetected,
  isOpen,
}) => {
  const [meetingLinkType, setMeetingLinkType] = useState('allDay'); // allDay | custom | embedded
  const [event, setEvent] = useState({});
  const [originalEvent, setOriginalEvent] = useState({});
  const [originalMeetingLinkType, setOriginalMeetingLinkType] = useState('allDay');
  const eventTitleInputRef = useRef(null);

  const dContext = useContext(DepartmentContext);
  const embeddedUiEnabled =
    dContext &&
    dContext.department &&
    dContext.department.VirtualMeetingMode === 'Internal' &&
    Boolean(dContext.department.enableEmbeddedMeetings);

  const { metaData } = scheduleData || {};
  const { MainRoomURL, MainRoomURLDetails } = metaData || {};
  const useAllDayLink = meetingLinkType === 'allDay';
  const useCustomLink = meetingLinkType === 'custom';
  const useEmbeddedLink = meetingLinkType === 'embedded';

  const selectMeetingLinkType = async (type, e) => {
    if (type === 'embedded' && !embeddedUiEnabled) {
      return;
    }
    if (dateIsInThePast && !willEditThePast) {
      const continueProcess = await fireConfirmationForEditingPastSchedule();
      if (!continueProcess) {
        return;
      }
    }
    e?.stopPropagation?.();

    setMeetingLinkType(type);
    if (type === 'allDay') {
      updateEvent('VirtualRoomType', 'AllDay');
      return;
    }
    if (type === 'custom') {
      updateEvent('VirtualRoomType', 'Custom');
      return;
    }
    if (type === 'embedded') {
      updateEvent('VirtualRoomType', 'Embedded');
      updateEvent('CustomMeetingUrl', REZRATE_EMBEDDED_MEETING_PLACEHOLDER);
      if (!event.CustomMeetingDetails || `${event.CustomMeetingDetails}`.trim() === '') {
        updateEvent('CustomMeetingDetails', 'RezRATE will generate embedded meetings automatically as needed.');
      }
    }
  };

  useEffect(() => {
    if (row) {
      const {
        CustomDurationInMinutes,
        CustomMeetingDetails,
        CustomMeetingTitle,
        CustomMeetingUrl,
        CustomPassingDurationInMinutes,
        VirtualRoomType,
        CustomRoomLabel,
      } = row;
      const { metaData = {} } = scheduleData || {};

      const duration = CustomDurationInMinutes != null ? CustomDurationInMinutes : metaData.StandardDurationInMinutes;
      const passingDuration =
        CustomPassingDurationInMinutes != null
          ? CustomPassingDurationInMinutes
          : metaData.StandardPassingDurationInMinutes;

      const initialEvent = {
        CustomDurationInMinutes: duration,
        CustomMeetingDetails,
        CustomMeetingTitle,
        CustomMeetingUrl,
        CustomPassingDurationInMinutes: passingDuration,
        VirtualRoomType: VirtualRoomType || 'AllDay',
        CustomRoomLabel,
      };

      setEvent(initialEvent);
      setOriginalEvent(clone(initialEvent));

      let initialMeetingLinkType = 'allDay';
      if (VirtualRoomType === 'Custom') {
        initialMeetingLinkType = 'custom';
      } else if (VirtualRoomType === 'Embedded') {
        initialMeetingLinkType = embeddedUiEnabled ? 'embedded' : 'custom';
      }
      setMeetingLinkType(initialMeetingLinkType);
      setOriginalMeetingLinkType(initialMeetingLinkType);
    }
  }, [JSON.stringify(row), embeddedUiEnabled]);

  // Detect changes and notify parent
  useEffect(() => {
    const eventChanged = JSON.stringify(event) !== JSON.stringify(originalEvent);
    const linkTypeChanged = meetingLinkType !== originalMeetingLinkType;
    const hasChanges = eventChanged || linkTypeChanged;

    if (onChangeDetected) {
      onChangeDetected(hasChanges);
    }
  }, [event, originalEvent, meetingLinkType, originalMeetingLinkType, onChangeDetected]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = setTimeout(() => {
      eventTitleInputRef.current && eventTitleInputRef.current.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen]);

  // useEffect(() => {
  //   if (useAllDayLink) {
  //     updateEvent('VirtualRoomType', 'AllDay');
  //   } else {
  //     updateEvent('VirtualRoomType', 'Custom');
  //   }
  // }, [useAllDayLink]);

  const updateEvent = (key, value) => {
    const newEvent = clone(event);
    newEvent[key] = value;
    setEvent(newEvent);
  };

  const hasSplitsOrCandidates = () => {
    const { headers } = scheduleData;
    let valueToReturn = false;

    const headerIDs = headers.map((header) => {
      return header.pk_ScheduleAssignment;
    });

    headerIDs.forEach((pk_ScheduleAssignment) => {
      const { ScheduleBlockEntries } = row[pk_ScheduleAssignment];
      if (ScheduleBlockEntries && ScheduleBlockEntries.length > 0) {
        if (ScheduleBlockEntries.length > 1) {
          valueToReturn = true;
        }

        if (ScheduleBlockEntries[0].Candidates && ScheduleBlockEntries[0].Candidates.length > 0) {
          valueToReturn = true;
        }
      }
    });

    return valueToReturn;
  };

  const addCustomEvent = () => {
    const newEvent = clone(event);
    newEvent.pk_Timeslot = row.pk_Timeslot;
    newEvent.fk_MeetingRoom = newEvent.RoomNumber;
    if (isRezrateEmbeddedMeetingUrl(newEvent.CustomMeetingUrl)) {
      newEvent.CustomMeetingUrl = REZRATE_EMBEDDED_MEETING_PLACEHOLDER;
    } else if (newEvent.CustomMeetingUrl) {
      newEvent.CustomMeetingUrl = urlWithHttpProtocol(newEvent.CustomMeetingUrl);
    }
    delete newEvent.RoomNumber;

    // newEvent.CustomMeetingTitle =
    //   newEvent.CustomMeetingTitle.toLowerCase() === 'break'
    //     ? `${newEvent.CustomMeetingTitle} `
    //     : newEvent.CustomMeetingTitle;

    // Reset unsaved changes flag before closing
    if (onChangeDetected) {
      onChangeDetected(false);
    }

    addRowCustomEvent(newEvent);
    hideHeaderPopover();
    setShowAddCustomPopover(false);
  };

  return (
    <div
      style={style.mainBody}
      onClick={async (e) => {
        if (dateIsInThePast && !willEditThePast) {
          const continueProcess = await fireConfirmationForEditingPastSchedule();
          if (!continueProcess) {
            return;
          }
        }
        e.stopPropagation();
      }}
    >
      <div style={{ ...style.simpleRow }}>
        <div style={{ ...style.simpleColumn, width: '50%', marginRight: 10 }}>
          <div style={style.simpleRow}>
            <div style={{ ...style.simpleColumn, width: '100%' }}>
              <Label style={style.inputLabel} for="eventTitle">
                Event Title
              </Label>
              <Input
                innerRef={eventTitleInputRef}
                type="text"
                invalid={!event.CustomMeetingTitle}
                style={style.titleField}
                name="eventTitle"
                placeholder="Event Title. . ."
                value={event.CustomMeetingTitle}
                onChange={(e) => {
                  updateEvent('CustomMeetingTitle', e.target.value);
                }}
              />
            </div>
          </div>

          <div style={style.simpleRow}>
            <div style={{ ...style.simpleColumn, width: '100%' }}>
              <Label style={style.inputLabel} for="roomNumber">
                Room
              </Label>
              <Input
                type="text"
                style={style.roomNumberField}
                name="roomNumber"
                id="roomNumber"
                value={event.CustomRoomLabel}
                placeholder="Room Label. . ."
                onChange={(e) => {
                  updateEvent('CustomRoomLabel', e.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <div style={{ ...style.simpleColumn, width: '50%' }}>
          <div style={style.simpleRow}>
            <div style={{ ...style.simpleColumn, width: '100%' }}>
              <Label style={style.inputLabel} for="eventDuration">
                Event Duration
              </Label>
              <Input
                type="number"
                style={style.roomNumberField}
                name="eventDuration"
                id="eventDuration"
                placeholder="Duration minutes. . ."
                value={event.CustomDurationInMinutes}
                onChange={(e) => {
                  let newValue = e.target.value;

                  if (newValue < 1) {
                    newValue = 1;
                  }

                  updateEvent('CustomDurationInMinutes', newValue);
                }}
              />
            </div>
          </div>
          <div style={style.simpleRow}>
            <div style={{ ...style.simpleColumn, width: '100%' }}>
              <Label style={style.inputLabel} for="eventDuration">
                Passing Period
              </Label>
              <Input
                type="number"
                style={style.roomNumberField}
                name="passingPeriod"
                id="passingPeriod"
                placeholder="Passing minutes. . ."
                value={event.CustomPassingDurationInMinutes}
                onChange={(e) => {
                  let newValue = e.target.value;

                  if (newValue < 0) {
                    newValue = 0;
                  }

                  updateEvent('CustomPassingDurationInMinutes', newValue);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={style.simpleRow}>
        <div style={{ ...style.simpleColumn, width: 'auto', marginRight: 20 }}>
          <Label style={{ cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center' }}>
            <Input
              type="radio"
              name="customEventMeetingLinkType"
              checked={useAllDayLink}
              onChange={(e) => selectMeetingLinkType('allDay', e)}
              style={{ marginRight: 8 }}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              Same as All Day Meeting
              <FontAwesomeIcon id={`useAllDayMeetingLabel`} icon={faInfoCircle} style={{ marginLeft: 6 }} />
              <UncontrolledTooltip target={`useAllDayMeetingLabel`}>
                Use the All Day Meeting link for this meeting.
              </UncontrolledTooltip>
            </span>
          </Label>
        </div>
        <div style={{ ...style.simpleColumn, width: 'auto', marginRight: 20 }}>
          <Label style={{ cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center' }}>
            <Input
              type="radio"
              name="customEventMeetingLinkType"
              checked={useCustomLink}
              onChange={(e) => selectMeetingLinkType('custom', e)}
              style={{ marginRight: 8 }}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              External URL
              <FontAwesomeIcon id="useCustomLinkLabel" icon={faInfoCircle} style={{ marginLeft: 6 }} />
              <UncontrolledTooltip target={`useCustomLinkLabel`}>
                Use a unique external link for this event.
              </UncontrolledTooltip>
            </span>
          </Label>
        </div>
        {embeddedUiEnabled ? (
          <div style={{ ...style.simpleColumn, width: 'auto' }}>
            <Label style={{ cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center' }}>
              <Input
                type="radio"
                name="customEventMeetingLinkType"
                checked={useEmbeddedLink}
                onChange={(e) => selectMeetingLinkType('embedded', e)}
                style={{ marginRight: 8 }}
              />
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                RezRATE Embedded Meeting (Unique)
                <FontAwesomeIcon id="useEmbeddedLinkLabel" icon={faInfoCircle} style={{ marginLeft: 6 }} />
                <UncontrolledTooltip target={`useEmbeddedLinkLabel`}>
                  Generate a unique RezRATE managed embedded meeting for this custom event.
                </UncontrolledTooltip>
              </span>
            </Label>
          </div>
        ) : null}
      </div>
      {/* <div style={{ ...style.simpleRow, paddingLeft: 45 }}>
        <Input
          type="textarea"
          invalid={!useAllDayLink && !event.CustomMeetingUrl}
          disabled={useAllDayLink}
          name="text"
          placeholder="Enter Custom Link. . ."
          value={event.CustomMeetingUrl}
          onChange={(e) => {
            updateEvent('CustomMeetingUrl', e.target.value);
          }}
        />
      </div> */}

      <div
        style={{
          ...style.simpleRow,
          justifyContent: 'left',
          // paddingLeft: 45,
          marginBottom: 10,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {' '}
          <Label style={{ ...style.inputLabel, textAlign: 'center' }} for="eventLink">
            {useAllDayLink
              ? 'All Day Meeting Link'
              : useEmbeddedLink
              ? 'RezRATE Embedded Meeting'
              : 'External Meeting Link'}
          </Label>
          {useAllDayLink && !MainRoomURL ? (
            <div
              style={{
                ...style.simpleColumn,
                width: '100%',
                backgroundColor: '#e9ecef',
                border: '1px solid red',
                color: '#495057',
                borderRadius: 5,
                paddingLeft: 10,
                height: 80,
              }}
            >
              <div>Missing All Day Meeting Link!</div>
              <div style={{ ...style.simpleRow, alignItems: 'stretch', height: 40 }}>
                <span style={{ marginRight: 0 }}>
                  {`The All Day Meeting Link can be configured by clicking the `}
                  <span style={{ display: 'inline-flex', alignItems: 'center', width: 20 }}>
                    <FontAwesomeIcon icon={faLink} />
                    <span style={{ position: 'relative' }}>
                      <FontAwesomeIcon
                        icon={faCog}
                        style={{ position: 'absolute', fontSize: 10, left: -6, top: 3, color: 'inherit' }}
                      />
                    </span>
                  </span>
                  {` button above schedule. . .`}
                </span>
              </div>
            </div>
          ) : (
            <Input
              type="textarea"
              invalid={(useCustomLink && !event.CustomMeetingUrl) || (useAllDayLink && !MainRoomURL)}
              rows={2}
              disabled={!useCustomLink}
              value={
                useAllDayLink
                  ? formatMeetingUrlForDisplay(MainRoomURL)
                  : useEmbeddedLink
                  ? formatMeetingUrlForDisplay(REZRATE_EMBEDDED_MEETING_PLACEHOLDER)
                  : event.CustomMeetingUrl || ' '
              }
              placeholder={useAllDayLink ? 'No Main Room URL. . .' : useEmbeddedLink ? '' : 'Enter Custom Link. . .'}
              onChange={(e) => {
                if (useCustomLink) {
                  updateEvent('CustomMeetingUrl', e.target.value);
                }
              }}
            />
          )}
        </div>
      </div>

      <div
        style={{
          ...style.simpleRow,
          justifyContent: 'left',
          // paddingLeft: 45,
          marginBottom: 10,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Label style={{ ...style.inputLabel, textAlign: 'center' }} for="eventLink">
            {useAllDayLink
              ? 'All Day Meeting Help'
              : useEmbeddedLink
              ? 'Embedded Meeting Details'
              : 'Custom Meeting Details'}
          </Label>
          <Input
            type="textarea"
            rows={4}
            disabled={!useCustomLink}
            value={
              useAllDayLink
                ? MainRoomURLDetails
                : useEmbeddedLink
                ? event.CustomMeetingDetails || 'RezRATE will generate embedded meetings automatically as needed.'
                : event.CustomMeetingDetails || ' '
            }
            placeholder={
              useAllDayLink ? 'No Main Room Details. . .' : useEmbeddedLink ? '' : 'No Custom Meeting Details. . .'
            }
            onChange={(e) => {
              if (useCustomLink) {
                updateEvent('CustomMeetingDetails', e.target.value);
              }
            }}
          />
        </div>
      </div>

      <div style={{ ...style.simpleRow, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...style.simpleColumn, width: 100 }}>
          <Button
            color="danger"
            style={{ ...style.genButton, marginBottom: 0, paddingLeft: null, justifyContent: 'center' }}
            onClick={() => {
              Swal.fire({
                title: 'Warning!',
                text: `Are you sure you want to delete this event? This cannot be undone.`,
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: 'red',
                confirmButtonText: `Delete`,
              }).then((result) => {
                if (result.value) {
                  // Reset unsaved changes flag before closing
                  if (onChangeDetected) {
                    onChangeDetected(false);
                  }

                  addRowCustomEvent({
                    pk_Timeslot: row.pk_Timeslot,
                    CustomDurationInMinutes: null,
                    CustomMeetingDetails: null,
                    CustomMeetingTitle: null,
                    CustomMeetingUrl: null,
                    CustomPassingDurationInMinutes: null,
                    VirtualRoomType: null,
                  });
                  hideHeaderPopover();
                  setShowAddCustomPopover(false);
                }
              });
            }}
          >
            <div style={{ ...style.spaceBetweenRow, margin: 0 }}>
              <FontAwesomeIcon icon={faTrash} />
              {/* <div style={style.buttonLabel}>Delete</div> */}
            </div>
          </Button>
        </div>
        {/* <div style={{ ...style.simpleColumn, width: '33.33%' }}>
          <Button
            color="danger"
            style={{ ...style.genButton, marginBottom: 0, paddingLeft: null, justifyContent: 'center' }}
            onClick={() => {
              setShowAddCustomPopover(false);
            }}
          >
            <div style={{ ...style.spaceBetweenRow, margin: 0 }}>
              <FontAwesomeIcon icon={faTimes} />
              <div style={style.buttonLabel}>Cancel</div>
            </div>
          </Button>
        </div> */}
        <div style={{ ...style.simpleColumn, width: 100 }}>
          <Button
            color="success"
            disabled={!event.CustomMeetingTitle}
            style={{ ...style.genButton, justifyContent: 'center', paddingLeft: null }}
            onClick={() => {
              if (hasSplitsOrCandidates() && !row.CustomMeetingTitle) {
                Swal.fire({
                  title: 'Splits and/or assigned Candidates detected!',
                  text:
                    'Proceeding with creating this Custom Event would clear out any assigned candidates and split cells in this row. Do you want to proceed anyway?',
                  icon: 'warning',
                  showCancelButton: true,
                  cancelButtonColor: 'gray',
                  confirmButtonText: 'Create Custom Event',
                }).then((result) => {
                  if (result.isConfirmed) {
                    addCustomEvent();
                  } else {
                  }
                });
              } else {
                addCustomEvent();
              }
            }}
          >
            <div style={{ ...style.spaceBetweenRow, margin: 0 }}>
              <FontAwesomeIcon icon={faCheck} />
              {/* <div style={style.buttonLabel}>OK</div> */}
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomEventPopover;
